import React from 'react';
import { useTheme } from '@emotion/react';
import { alpha } from '@mui/material/styles';
import PropTypes from 'prop-types';
import { Box } from '@mui/system';
import {
  Checkbox,
  FormControlLabel,
  Typography,
  Chip,
  Select,
  MenuItem,
  TextField,
  Grid,
  Paper,
  Divider,
} from '@mui/material';
import { tokens } from '../theme';
import FileUpload from './fileUpload';

/**
 * SkillSelector
 * Reusable component to display a list of skills with selection, per-skill score and comment.
 *
 * Props:
 * - title: string (section title, used when unitName/unitCode not provided)
 * - unitName?: string (optional, shows as header before list)
 * - unitCode?: string (optional, appended after name)
 * - required: boolean (show *)
 * - skills: array of { skillCode, skill, discipline, description }
 * - selectedSkills: array of selected skillCodes
 * - evaluationLevels: object map { [skillCode]: number|string }
 * - evaluationScores: object map { [skillCode]: number }
 * - evaluationComments: object map { [skillCode]: string }
 * - onToggleSkill: (skillCode: string, checked: boolean) => void
 * - onChangeLevel: (skillCode: string, level: number|string) => void
 * - onChangeScore: (skillCode: string, score: number) => void
 * - onChangeComment: (skillCode: string, comment: string) => void
 * - levelOptions: array of { value: number|string, label: string }
 * - maxHeight: number | string for scroll container
 */
export default function SkillSelector({
  title = 'Skills to evaluate',
  unitName,
  unitCode,
  required = false,
  skills = [],
  selectedSkills = [],
  evaluationLevels = {},
  evaluationScores = {},
  evaluationComments = {},
  evaluationFiles = {},
  onToggleSkill,
  onChangeLevel,
  onChangeScore,
  onChangeComment,
  onChangeFiles,
  levelOptions = [
    { value: 'awareness', label: '1 - Awareness' },
    { value: 'skilled_application', label: '2 - Skilled Application' },
    { value: 'management', label: '3 - Management' },
  ],
  maxHeight = 240,
  showFileUpload = true,
}) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isSelected = React.useCallback(
    (code) => selectedSkills?.includes(code),
    [selectedSkills]
  );

  // Consistent grid sizing helper (similar to AddPoste/AddEvaluation)
  function getFieldGridProps(name) {
    switch (name) {
      case 'level':
      case 'score':
        return { xs: 12, md: 4, lg: 4 };
      case 'comment':
        return { xs: 12, md: 4, lg: 4 };
      default:
        return { xs: 12, md: 4, lg: 4 };
    }
  }

  const headerText = unitName ? `${unitName}${unitCode ? ` - ${unitCode}` : ''}` : title;

  return (
    <Box>
      {headerText && (
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          {headerText} {required ? '*' : ''}
        </Typography>
      )}

      <Paper variant="outlined" sx={{ p: 2, height:'100%', overflowY: 'auto' }}>
        <Grid container direction="column" spacing={2}>
          {skills.map((skill) => {
            const selected = isSelected(skill.skillCode);
            const levelValue = evaluationLevels?.[skill.skillCode] ?? '';
            const scoreValue = evaluationScores?.[skill.skillCode] ?? '';
            const commentValue = evaluationComments?.[skill.skillCode] ?? '';

            return (
              <Grid item key={skill.skillCode}>
                <Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selected}
                          onChange={(e) => onToggleSkill?.(skill.skillCode, e.target.checked)}
                        />
                      }
                      label={
                        <Box sx={{ cursor: 'pointer', width: '100%' }}>
                          <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Box>
                              <Typography variant="subtitle2" fontWeight={600} component="span">
                                {skill.skill}
                              </Typography>
                              {/* Hide skillCode from display */}
                            </Box>
                            {skill.discipline && (
                              <Chip size="small" variant="outlined" label={skill.discipline} />
                            )}
                          </Box>
                          {skill.description && (
                            <Typography variant="body2" color="text.secondary" mt={0.5}>
                              {skill.description}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </Box>

                  {selected && (
                    <Box mt={1.5} ml={5} p={2} borderRadius={1}>
                      <Grid container spacing={2}>
                        <Grid item size={{ ...getFieldGridProps('level') }}>
                          <Typography variant="body2" fontWeight={500} gutterBottom>
                            Skill level {required ? '*' : ''}
                            {skill.required_level && (
                              <Typography component="span" variant="caption" sx={{ ml: 1, color: 'primary.main', fontStyle: 'italic' }}>
                                (Required: {skill.required_level})
                              </Typography>
                            )}
                          </Typography>
                          <Select
                            fullWidth
                            value={levelValue}
                            displayEmpty
                            onChange={(e) => {
                              const v = e.target.value;
                              onChangeLevel?.(skill.skillCode, v);
                            }}
                          >
                            <MenuItem value="">
                              <em>Level</em>
                            </MenuItem>
                            {levelOptions.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </Grid>
                        <Grid item size={{ ...getFieldGridProps('score') }}>
                          <Typography variant="body2" fontWeight={500} gutterBottom>
                            Score {required ? '*' : ''}
                            {(skill.min_score !== undefined || skill.max_score !== undefined) && (
                              <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary', fontStyle: 'italic' }}>
                                ({skill.min_score !== undefined ? skill.min_score : '?'} - {skill.max_score !== undefined ? skill.max_score : '?'})
                              </Typography>
                            )}
                          </Typography>
                          <TextField
                            fullWidth
                            type="number"
                            inputProps={{ 
                              min: skill.min_score !== undefined ? skill.min_score : 0,
                              max: skill.max_score !== undefined ? skill.max_score : undefined
                            }}
                            placeholder={
                              skill.min_score !== undefined && skill.max_score !== undefined
                                ? `Score (${skill.min_score}-${skill.max_score})`
                                : "Numeric score"
                            }
                            value={scoreValue}
                            onChange={(e) => {
                              const v = e.target.value;
                              const parsed = v === '' ? '' : Number.parseFloat(v);
                              onChangeScore?.(skill.skillCode, parsed);
                            }}
                          />
                        </Grid>
                        <Grid item size={{ ...getFieldGridProps('comment') }}>
                          <Typography variant="body2" fontWeight={500} gutterBottom>
                            Specific comment
                          </Typography>
                          <TextField
                            fullWidth
                            placeholder="Comment for this skill..."
                            value={commentValue}
                            onChange={(e) => onChangeComment?.(skill.skillCode, e.target.value)}
                          />
                        </Grid>
                        {/* Full-width level description (shown before file upload) */}
                        <Grid item size={{ xs: 12 }}>
                          {levelValue && (
                            (() => {
                              const bg = levelValue === 'awareness'
                                ? alpha(colors.skyBlue[400], 0.1)
                                : levelValue === 'skilled_application'
                                  ? alpha(colors.Orange[400], 0.1)
                                  : alpha(colors.redAccent[400], 0.1);
                              const border = levelValue === 'awareness'
                                ? `1px solid ${alpha(colors.skyBlue[400], 0.2)}`
                                : levelValue === 'skilled_application'
                                  ? `1px solid ${alpha(colors.Orange[400], 0.2)}`
                                  : `1px solid ${alpha(colors.redAccent[400], 0.2)}`;
                              return (
                                <Box p={2} sx={{ backgroundColor: bg, border, borderRadius: 1 }}>
                                  <Typography variant="body2" sx={{ color: colors.grey[800] }}>
                                    {(() => {
                                      if (levelValue === 'awareness') return skill.awareness_description || '';
                                      if (levelValue === 'skilled_application') return skill.skilled_application_description || '';
                                      if (levelValue === 'management') return skill.management_description || '';
                                      return '';
                                    })()}
                                  </Typography>
                                </Box>
                              );
                            })()
                          )}
                        </Grid>
                        {/* File upload for this skill (optional) */}
                        {showFileUpload && (
                            <Grid item size={{ xs: 12 }}>
                              <Typography variant="body2" fontWeight={500} gutterBottom>
                                Evidence files
                              </Typography>
                              <FileUpload
                                  selectedFiles={evaluationFiles?.[skill.skillCode] || []}
                                  setSelectedFiles={(files) => onChangeFiles?.(skill.skillCode, files)}
                                  onFileSelect={(files) => onChangeFiles?.(skill.skillCode, Array.isArray(files) ? files : [files])}
                                  label="Drag & drop"
                                  description="Images, videos, documents"
                                  acceptedFormats="JPG, PNG, PDF, MP4, DOCX"
                                  browseButtonLabel="Upload"
                                  multiple={true}
                              />
                            </Grid>
                        )}
                      </Grid>
                    </Box>
                  )}
                </Box>
                <Divider sx={{ mt: 2 }} />
              </Grid>
            );
          })}
        </Grid>
      </Paper>
    </Box>
  );
}

SkillSelector.propTypes = {
  title: PropTypes.string,
  required: PropTypes.bool,
  skills: PropTypes.arrayOf(
    PropTypes.shape({
      skillCode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      skill: PropTypes.string.isRequired,
      discipline: PropTypes.string,
      description: PropTypes.string,
    })
  ),
  selectedSkills: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  evaluationScores: PropTypes.object,
  evaluationComments: PropTypes.object,
  onToggleSkill: PropTypes.func,
  onChangeScore: PropTypes.func,
  onChangeComment: PropTypes.func,
  scoreOptions: PropTypes.arrayOf(
    PropTypes.shape({ value: PropTypes.number.isRequired, label: PropTypes.string.isRequired })
  ),
  maxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};
